import { useCallback } from "react"
import { useAuthProvider } from "../../Providers/AuthProvider"
import { useWorkspacesProvider } from "../../Providers/WorkspacesProvider"

// The original PDF behind a source, opened at the cited page. Clara serves it behind the
// same token as the chat, so a plain link cannot carry it: the file is fetched, then shown
// from a blob URL with #page=N, which the browser's PDF viewer honours.

const CLARA_API = window.env?.REACT_APP_CLARA_API_URL

// Kept long enough for the viewer's own save and print to still work.
const REVOKE_AFTER_MS = 10 * 60 * 1000

export function canOpenOriginal(source) {
	return Boolean(source?.has_original && source?.document_id != null)
}

export function originalPageUrl(objectUrl, page) {
	return page ? `${objectUrl}#page=${page}` : objectUrl
}

export async function openOriginalPdf({ api, token, workspace, documentId, page }) {
	// Opened before the fetch, while the click still counts as the user's: a window
	// opened after an await is blocked as a popup.
	const tab = window.open("", "_blank")
	try {
		const response = await fetch(`${api}/documents/${documentId}/file?workspace_name=${encodeURIComponent(workspace)}`, {
			headers: { Authorization: token },
		})
		if (!response.ok) throw new Error((await response.text()) || `status ${response.status}`)
		const url = URL.createObjectURL(await response.blob())
		if (tab) tab.location.href = originalPageUrl(url, page)
		setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS)
	} catch (e) {
		tab?.close()
		window.alert(`Could not open the original PDF: ${e.message}`)
	}
}

export function useOpenOriginal() {
	const { getToken } = useAuthProvider()
	const { selectedWorkspace } = useWorkspacesProvider()
	return useCallback(
		(documentId, page) => openOriginalPdf({ api: CLARA_API, token: getToken(), workspace: selectedWorkspace, documentId, page }),
		[getToken, selectedWorkspace],
	)
}
